/**
 * BuilderCanvas - Renders the email document with rows and columns
 * Supports drag-and-drop for reordering rows and blocks
 */

import React from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { EmailDocument, ContentBlock, Selection, RowLayout } from './types';
import { SortableRow } from './SortableRow';
import { DroppableColumn } from './DroppableColumn';
import { Plus, Square, Columns2, Columns3 } from 'lucide-react';

interface BuilderCanvasProps {
  document: EmailDocument;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onClearSelection: () => void;
  onMoveRow: (fromIndex: number, toIndex: number) => void;
  onMoveBlock: (blockId: string, targetColumnId: string, targetIndex: number) => void;
  onAddRow: (layout: RowLayout) => void;
  onDeleteRow: (rowId: string) => void;
  onDuplicateRow: (rowId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onUpdateBlock: (blockId: string, updates: Partial<ContentBlock>) => void;
  activeRowId?: string | null;
  overRowId?: string | null;
}

export const BuilderCanvas: React.FC<BuilderCanvasProps> = ({
  document,
  selection,
  onSelect,
  onClearSelection,
  onMoveRow,
  onMoveBlock,
  onAddRow,
  onDeleteRow,
  onDuplicateRow,
  onDeleteBlock,
  onUpdateBlock,
  activeRowId,
  overRowId,
}) => {
  const [showRowMenu, setShowRowMenu] = React.useState(false);
  const [hoveredBlockId, setHoveredBlockId] = React.useState<string | null>(null);
  
  // Calculate drop indicator position
  const getDropIndicatorPosition = (rowId: string, rowIndex: number) => {
    if (!activeRowId || !overRowId || activeRowId === overRowId) {
      return { above: false, below: false };
    }
    
    // Only show indicator on the row being hovered over
    if (rowId !== overRowId) {
      return { above: false, below: false };
    }
    
    const activeIndex = document.rows.findIndex(r => r.id === activeRowId);
    const overIndex = rowIndex;
    
    // If dragging down, show indicator below the hovered row
    // If dragging up, show indicator above the hovered row
    if (activeIndex < overIndex) {
      return { above: false, below: true };
    } else {
      return { above: true, below: false };
    }
  };

  const rowLayoutOptions: { layout: RowLayout; icon: React.ReactNode; label: string }[] = [
    { layout: '1-col', icon: <Square className="w-4 h-4" />, label: '1 Column' },
    { layout: '2-col', icon: <Columns2 className="w-4 h-4" />, label: '2 Columns' },
    { layout: '3-col', icon: <Columns3 className="w-4 h-4" />, label: '3 Columns' },
  ];

  // Handle click on empty canvas area to clear selection
  const handleCanvasClick = (e: React.MouseEvent) => {
    // Only clear if clicking directly on the canvas background
    if (e.target === e.currentTarget) {
      onClearSelection();
    }
  };

  return (
    <div 
      className="flex-1 overflow-y-auto p-6"
      style={{ 
        backgroundColor: document.globalStyles.backgroundColor,
        colorScheme: 'light',
      }}
      onClick={handleCanvasClick}
    >
      {/* Email Container - Full width */}
      <div 
        className="shadow-lg rounded-lg"
        style={{ backgroundColor: '#ffffff' }}
        onClick={handleCanvasClick}
      >
        <SortableContext
          items={document.rows.map(r => r.id)}
          strategy={verticalListSortingStrategy}
        >
          {/* Sortable container with proper spacing for drop detection */}
          <div className="min-h-[100px]">
            {document.rows.length === 0 ? (
              /* Empty state */
              <div className="p-12 text-center" style={{ backgroundColor: '#ffffff' }}>
                <div className="mb-4" style={{ color: '#9ca3af' }}>
                  <Plus className="w-12 h-12 mx-auto opacity-50" />
                </div>
                <p className="mb-4" style={{ color: '#6b7280' }}>Start building your email</p>
                <p className="text-sm mb-6" style={{ color: '#9ca3af' }}>Add a row to get started</p>
                <div className="flex justify-center gap-2">
                  {rowLayoutOptions.map(({ layout, icon, label }) => (
                    <button
                      key={layout}
                      onClick={() => onAddRow(layout)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      {icon}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Rows - clean rendering matching email output */
              <div className="pl-12" style={{ backgroundColor: 'transparent' }}>
                {document.rows.map((row, index) => {
                  const dropIndicator = getDropIndicatorPosition(row.id, index);
                  // Check if any block in this row is selected or hovered
                  const blockIdsInRow = row.columns.flatMap(col => col.blocks.map(b => b.id));
                  const hasSelectedBlock = selection.type === 'block' && selection.blockId !== undefined && blockIdsInRow.includes(selection.blockId);
                  const hasHoveredBlock = hoveredBlockId !== null && blockIdsInRow.includes(hoveredBlockId);
                  const hasActiveBlock = hasSelectedBlock || hasHoveredBlock;
                  return (
                  <SortableRow
                    key={row.id}
                    row={row}
                    isSelected={selection.type === 'row' && selection.rowId === row.id}
                    hasSelectedBlock={hasActiveBlock}
                    onSelect={() => onSelect({ type: 'row', rowId: row.id })}
                    onDelete={() => onDeleteRow(row.id)}
                    onDuplicate={() => onDuplicateRow(row.id)}
                    showDropIndicatorAbove={dropIndicator.above}
                    showDropIndicatorBelow={dropIndicator.below}
                  >
                    {/* Columns inside the row - flex for side-by-side */}
                    <div className="flex">
                      {row.columns.map((column, colIndex) => (
                        <DroppableColumn
                          key={column.id}
                          column={column}
                          rowId={row.id}
                          selection={selection}
                          onSelect={onSelect}
                          onDeleteBlock={onDeleteBlock}
                          onUpdateBlock={onUpdateBlock}
                          onMoveBlock={onMoveBlock}
                          onBlockHover={setHoveredBlockId}
                          isFirstRow={index === 0}
                          isLastColumn={colIndex === row.columns.length - 1 && row.columns.length > 1}
                        />
                      ))}
                    </div>
                  </SortableRow>
                  );
                })}
              </div>
            )}
          </div>
        </SortableContext>

        {/* Add Row Button */}
        {document.rows.length > 0 && (
          <div 
            className="relative border-t border-dashed p-4"
            style={{ borderColor: '#d1d5db', backgroundColor: '#ffffff' }}
          >
            <button
              onClick={() => setShowRowMenu(!showRowMenu)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg transition-colors"
              style={{ color: '#9ca3af' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2563eb';
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#9ca3af';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Plus className="w-5 h-5" />
              <span className="text-sm">Add Row</span>
            </button>

            {showRowMenu && (
              <div 
                className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 rounded-lg shadow-xl p-2 flex gap-2 z-10"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
              >
                {rowLayoutOptions.map(({ layout, icon, label }) => (
                  <button
                    key={layout}
                    onClick={() => {
                      onAddRow(layout);
                      setShowRowMenu(false);
                    }}
                    className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors"
                    style={{ color: '#374151' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title={label}
                  >
                    {icon}
                    <span className="text-xs" style={{ color: '#6b7280' }}>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

